// IndexedDB-based image caching service
// Caches images locally for faster loading and offline access

const DB_NAME = 'portfolio-image-cache';
const DB_VERSION = 1;
const STORE_NAME = 'images';
const CACHE_EXPIRY_DAYS = 7;

let db = null;

/**
 * Initialize the IndexedDB database
 */
const initDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: 'url' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
};

/**
 * Get a cached image by URL
 * @param {string} url - The image URL
 * @returns {Promise<Blob|null>} - The cached blob or null if not found/expired
 */
export const getCachedImage = async (url) => {
  try {
    const database = await initDB();

    return new Promise((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(url);

      request.onerror = () => {
        console.error('Failed to get cached image:', request.error);
        resolve(null);
      };

      request.onsuccess = () => {
        const result = request.result;

        if (!result) {
          resolve(null);
          return;
        }

        // Check if cache has expired
        const expiryTime = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
        if (Date.now() - result.timestamp > expiryTime) {
          // Cache expired, delete it
          deleteFromCache(url);
          resolve(null);
          return;
        }

        resolve(result.blob);
      };
    });
  } catch (error) {
    console.error('Error getting cached image:', error);
    return null;
  }
};

/**
 * Cache an image
 * @param {string} url - The image URL
 * @param {Blob} blob - The image blob to cache
 */
export const cacheImage = async (url, blob) => {
  try {
    const database = await initDB();

    return new Promise((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      const request = store.put({
        url,
        blob,
        timestamp: Date.now()
      });

      request.onerror = () => {
        console.error('Failed to cache image:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  } catch (error) {
    console.error('Error caching image:', error);
  }
};

/**
 * Delete an image from cache
 * @param {string} url - The image URL to delete
 */
const deleteFromCache = async (url) => {
  try {
    const database = await initDB();

    return new Promise((resolve) => {
      const transaction = database.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      store.delete(url);
      resolve();
    });
  } catch (error) {
    console.error('Error deleting from cache:', error);
  }
};

/**
 * Clear all expired cache entries
 */
export const clearExpiredCache = async () => {
  try {
    const database = await initDB();
    const expiryTime = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    const cutoffTime = Date.now() - expiryTime;

    return new Promise((resolve) => {
      const transaction = database.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('timestamp');
      const range = IDBKeyRange.upperBound(cutoffTime);

      const request = index.openCursor(range);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          store.delete(cursor.primaryKey);
          cursor.continue();
        } else {
          resolve();
        }
      };

      request.onerror = () => {
        console.error('Failed to clear expired cache:', request.error);
        resolve();
      };
    });
  } catch (error) {
    console.error('Error clearing expired cache:', error);
  }
};

/**
 * Clear all cached images
 */
export const clearAllCache = async () => {
  try {
    const database = await initDB();

    return new Promise((resolve) => {
      const transaction = database.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      store.clear();
      resolve();
    });
  } catch (error) {
    console.error('Error clearing all cache:', error);
  }
};

/**
 * Get cache statistics
 * @returns {Promise<{count: number, totalSize: number}>}
 */
export const getCacheStats = async () => {
  try {
    const database = await initDB();

    return new Promise((resolve) => {
      const transaction = database.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        const items = request.result || [];
        const totalSize = items.reduce((sum, item) => sum + (item.blob?.size || 0), 0);
        resolve({
          count: items.length,
          totalSize
        });
      };

      request.onerror = () => {
        resolve({ count: 0, totalSize: 0 });
      };
    });
  } catch (error) {
    console.error('Error getting cache stats:', error);
    return { count: 0, totalSize: 0 };
  }
};

/**
 * Fetch and cache an image
 * @param {string} url - The image URL to fetch
 * @returns {Promise<string>} - Object URL for the cached image
 */
export const fetchAndCacheImage = async (url) => {
  // Check cache first
  const cachedBlob = await getCachedImage(url);
  if (cachedBlob) {
    return URL.createObjectURL(cachedBlob);
  }

  // Fetch from network
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }

    const blob = await response.blob();

    // Cache the image
    await cacheImage(url, blob);

    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error fetching image:', error);
    // Return original URL as fallback
    return url;
  }
};

export const imageCacheService = {
  getCachedImage,
  cacheImage,
  clearExpiredCache,
  clearAllCache,
  getCacheStats,
  fetchAndCacheImage
};
